import {type ReactNode, type Ref} from 'react';

import type {
  HighlightSource,
  SetHighlight,
  SetHighlights,
} from '../../state/interactState/highlightSource';
import {type IssueLine} from '../../state/issues/types';
import {type Data} from '../../state/runs/types';
import {type ColorPaletteOption} from '../../util/colors';
import {type LegendPosition} from '../../util/plotHelpers/legendTypes';
import {type PlotFontSizeOrAuto} from '../../util/plotHelpers/plotFontSize';
import {type PlotType} from '../../util/plotHelpers/types';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {type ChartData} from '../vis/chartSeries';
import {
  type DerivedPointVisualizationOption,
  type ExcludeOutliersValues,
  type SinglePointRenderMode,
} from '../WorkspaceDrawer/Settings/types';
import {type IssueMarkerStateInputs} from './issueMarkerViewProps';
import {type LinePlotSettings} from './LinePlotSettingsContext';
import {type RunsLinePlotConfig} from './types';
import {type GraphZoom as PreparedGraphZoom} from './zoom/getGraphZoom';

/**
 * Transitional renderer inputs. Saved config, query Data, and plotSettings
 * remain explicit legacy fields until their feature groups are migrated.
 * Hosts preserve each group's identity while its fields are unchanged.
 */
/** Resolved axes. The saved key remains diagnostic-only. */
export type GraphAxis = {
  outlierPolicy: ExcludeOutliersValues | undefined;
  xKey: string;
  xIsDatetime: boolean;
  xTitle?: string;
  xScale: 'linear' | 'log';
  yTitle?: string;
  yScale: 'linear' | 'log';
};

export type GraphSelection = {
  metrics?: string[];
  /** True when an expression entry exists, including an empty editor entry. */
  hasExpressions: boolean;
  /** Null disables the existing mounted regex-edit grace window. */
  regexDataKey: string | null;
};

/** Display policy. Measured auto font size and data-dependent bar/scatter decisions stay local. */
export type GraphAppearance = {
  fontSize: PlotFontSizeOrAuto;
  isDarkMode: boolean;
};
/** Requested mode and effective fidelity preserve their distinct existing consumers. */
export type GraphRendering = {
  exportMode: boolean;
  forceReactVis: boolean;
  isFullFidelity: boolean;
  pointStrategy: DerivedPointVisualizationOption;
  plotType: PlotType | undefined;
  allowUPlot: boolean;
};

/** Omission hides the title; an absent action leaves static text. */
export type GraphTitleFeature = {
  text: string;
  renderHighlightedText?: (text: string) => ReactNode;
  onActivate?: () => void;
};

export const DEFAULT_GRAPH_LEGEND_POSITION: LegendPosition = 'north';

/** Omission disables the legend; an empty group enables adaptive defaults. */
export type GraphLegendFeature = {
  colorPalette?: ColorPaletteOption;
  colorRunNames?: boolean;
  isSingleRun: boolean;
  onHighlight?: (
    runId: string | undefined,
    metricName: string | undefined
  ) => void;
  position?: LegendPosition;
  resolveRunLink?: (target: GraphLegendLinkTarget) => string | undefined;
  show?: boolean;
};

export type GraphRenderProps = {
  /** Stable presentation policy, separate from numeric geometry. */
  appearance: GraphAppearance;
  /** Effective rendering choices, requested fidelity, and SVG export. */
  rendering: GraphRendering;
  /** Host-selected facts for diagnostics and regex settling, independent of query envelopes. */
  errors: GraphErrors;
  loading: GraphLoading;
  /** Readiness gates the provider; loading retains stale data and existing error precedence. */
  /** Resolved axis settings and titles, stable while their values are unchanged. */
  axis: GraphAxis;
  fallbacks?: GraphFallbacks;
  /** Raw saved key used only in diagnostics and test attributes. */
  /** Saved options, including the raw xAxis used by diagnostics. */
  config: Omit<
    RunsLinePlotConfig,
    | 'xAxis'
    | 'xAxisIsDatetime'
    | 'xAxisTitle'
    | 'xLogScale'
    | 'yAxisTitle'
    | 'yLogScale'
    | 'fontSize'
    | 'legendPosition'
    | 'useMetricRegex'
    | 'metricRegex'
    | 'yAxisMin'
    | 'yAxisMax'
    | 'metrics'
    | 'expressions'
    | 'chartTitle'
  >;
  /** Numeric identity is independent of descriptions and drawing style. */
  series: ChartData['series'];
  seriesStylesById: ChartData['seriesStylesById'];
  seriesMetadataById: ChartData['seriesMetadataById'];
  /** Transitional notice counts; no histories sentinel is required. */
  data: Pick<
    Data,
    | 'fetchedRunCount'
    | 'fetchedRunCountByRunsetId'
    | 'hasSubsampledGroups'
    | 'totalGroupedIncludedRuns'
  >;
  /** Suppresses editing guidance in legend warnings. */
  readOnly?: boolean;
  /** Tooltip run-name truncation policy. */
  runNameTruncationType?: TruncationType;
  /**
   * Delay Render opt-in for panels with capped system metrics, from
   * PanelSystemMetricsContext. A cached group: `isDelayed` is state, and
   * `setAllowUncappedMetrics` is a stable setter that Graph calls with true
   * when the user clicks Load anyway. Changing `isDelayed` swaps the plot for
   * the warning. Graph adds the title from config and `panelId`.
   */
  cappedPanel: {
    isDelayed: boolean;
    setAllowUncappedMetrics: (allow: boolean) => void;
  };
  /** Optional legend configuration; shared metric mode remains independent. */
  legend?: GraphLegendFeature;
  /**
   * Cross-panel highlight: a source over the Redux `interactState` highlight
   * and its two writers. GraphContainer takes the store's one source and the
   * writers from the `interactState` hooks. All three are stable for the
   * panel's life, so a highlight change never re-renders Graph; readers below
   * subscribe to the axes they draw. Hovering a legend run calls
   * `setHighlight`, and moving the pointer over the chart calls
   * `setHighlights`. The writers must update the source, or the crosshair
   * never shows its flag. Panels that share a crosshair share one source.
   */
  highlight: {
    setHighlight: SetHighlight;
    setHighlights: SetHighlights;
    source: HighlightSource;
  };
  /**
   * Issue lines drawn on the chart and positioned as markers above it, from
   * GraphContainer's caller. Per-render data: a new array re-renders Graph,
   * the chart, and the markers. Defaults to none.
   */
  issueLines?: IssueLine[];
  /**
   * Issue state and actions for the markers above the plot. GraphContainer
   * reads the issues store, the route's entity and project for analytics, the
   * workspace drawer, and the query zoom's x bounds. A cached group: the
   * count, flags, and bounds are state; `hoveredIssueSource` is a stable
   * source; the functions are stable. Hovering a marker calls
   * `handleIssueInteraction`, and clicking one opens the run issues drawer
   * and calls `setSelectedIssue`. A state change re-renders Graph and the
   * markers; a hovered-issue change re-renders only the markers whose state
   * changes. Marker layout comes from icon positioning inside Graph.
   */
  issueMarkers: IssueMarkerStateInputs;
  /**
   * ID of the mounted panel, from PanelIDContext. Stable for the panel's
   * life and unique per mounted panel: it keys the error panel's RUM
   * de-duplication, the zoom usage counter, and chart telemetry, and is sent
   * with the Delay Render RUM actions. The only copy in GraphRenderProps.
   */
  panelId: string;
  /** Host-resolved title text, search decoration, and optional activation. */
  title?: GraphTitleFeature;
  /**
   * Settings for Graph, the renderers, the crosshair, the legend, and the
   * legend warning; the one source for each. GraphContainer resolves them
   * from the panel config, workspace and section settings, flags, color mode,
   * and the nav route. A cached group: its identity changes only when a field
   * does. GraphWrapper provides it inside the unit through
   * LinePlotSettingsProvider, so a change re-renders Graph and every
   * useLinePlotSettings reader; the legend and warning views re-render only
   * when their own fields change. It also holds one event,
   * `onSnapMediaPanelsToStep`, and one getter, `getNamedWorkspaceSearch`;
   * both must be stable.
   */
  plotSettings: Omit<
    LinePlotSettings,
    | 'xAxis'
    | 'xAxisIsDatetime'
    | 'xAxisTitle'
    | 'xLogScale'
    | 'yAxisTitle'
    | 'yLogScale'
    | keyof GraphAppearance
    | keyof GraphRendering
    | 'pointVisualizationMethod'
    | 'uplotEnabled'
  >;
  /**
   * Whether the panel fell back to sampling, and why, from
   * RunsLinePlotContext. A cached group; a change re-renders Graph and the
   * sampling warning only.
   */
  samplingWarning: {
    isSamplingFallback: boolean;
    warningMessages: string[];
  };
  /**
   * Ref Graph attaches to the layout grid so the host can measure the panel,
   * from PanelInteractionContext. Stable for the panel's life; null is
   * accepted.
   */
  sizeRef: Ref<HTMLDivElement>;
  /** Controlled displayed domains and semantic gestures; host preparation owns effects. */
  zoom: GraphZoom;
};

export type GraphFallbacks = {
  barYMin?: number | null;
  barYMax?: number | null;
  singlePointStyle?: SinglePointRenderMode;
};

/** Displayed domains and dedicated zoom actions; query flags retain query-space meaning. */
export type GraphZoom = {
  isActive: boolean;
  isZooming: boolean;
  isZoomedXMax: boolean;
  isZoomedXMin: boolean;
  onReset?: () => void;
  onZoom: PreparedGraphZoom['onZoom'];
  xDomain: PreparedGraphZoom['xDomain'];
  yDomain: PreparedGraphZoom['yDomain'];
};

export type GraphLegendLinkTarget = {legendHref?: string};

export type GraphErrors = {
  hasExpressions: boolean;
  hasUnexpectedError?: boolean;
  metricKeys?: string[];
  savedXKey?: string;
};
export type GraphLoading = {
  isActive: boolean;
  isReady: boolean;
  regexSettlingKey: string | null;
};
